import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  MessageSquare,
  Zap,
  ArrowRight,
  Cloud,
  Check,
  CircleDollarSign
} from 'lucide-react';
import { Button } from './ui/button';
import { heroData } from '../mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const iconMap = {
  BadgeCheck: ShieldCheck,
  MessageCircle: MessageSquare,
  Zap: Zap
};

const Hero = () => {
  const { ref: heroRef, isVisible } = useScrollAnimation();
  const navigate = useNavigate();

  const scrollToServices = () => {
    const el = document.getElementById('servicios');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#0A1124] flex flex-col justify-between pt-24 sm:pt-28 lg:pt-32">
      {/* Retícula geométrica sutil (Grid del prototipo UX) */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:56px_56px] pointer-events-none"></div>

      {/* Iluminación ambiental de fondo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-[520px] h-[520px] bg-[#00C875]/[0.06] rounded-full blur-[120px]"></div>
        <div className="absolute bottom-1/4 left-1/6 w-[480px] h-[480px] bg-blue-500/[0.06] rounded-full blur-[120px]"></div>
      </div>

      {/* Contenido Principal a 2 Columnas */}
      <div
        ref={heroRef}
        className="relative z-10 max-w-7xl w-full mx-auto px-6 sm:px-8 lg:px-12 pt-10 pb-12 sm:pt-14 sm:pb-16 lg:pt-16 lg:pb-20 my-auto"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Columna Izquierda: Mensaje Principal y CTAs */}
          <div className="lg:col-span-7 text-left">
            {/* Pill superior */}
            <div
              className={`inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-[#00C875]/[0.08] border border-[#00C875]/30 mb-7 scroll-animate ${
                isVisible ? 'in-view' : ''
              }`}
            >
              <span className="w-2 h-2 bg-[#00C875] rounded-full animate-pulse"></span>
              <span className="text-xs sm:text-sm text-[#00C875] font-medium tracking-wide">
                {heroData.tagline}
              </span>
            </div>

            {/* Título Principal */}
            <h1
              className={`text-4xl sm:text-5xl md:text-6xl lg:text-[62px] font-bold text-white leading-[1.08] tracking-tight mb-6 scroll-animate delay-200 ${
                isVisible ? 'in-view' : ''
              }`}
            >
              {heroData.mainHeadline}
            </h1>

            {/* Subtítulo */}
            <p
              className={`text-base sm:text-lg md:text-xl text-slate-300/90 leading-relaxed mb-10 max-w-2xl scroll-animate delay-300 ${
                isVisible ? 'in-view' : ''
              }`}
            >
              {heroData.subHeadline}
            </p>

            {/* Botones de Acción */}
            <div
              className={`flex flex-col sm:flex-row gap-4 mb-12 scroll-animate delay-400 ${
                isVisible ? 'in-view' : ''
              }`}
            >
              <Button
                size="lg"
                onClick={() => navigate('/diagnostico-gratuito')}
                className="bg-[#00C875] hover:bg-[#00E082] text-[#041E13] font-semibold px-7 py-6 text-base rounded-xl shadow-[0_0_28px_rgba(0,200,117,0.35)] hover:shadow-[0_0_36px_rgba(0,200,117,0.55)] hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2.5"
              >
                <span>{heroData.primaryCTA.text}</span>
                <ArrowRight className="w-4 h-4 stroke-[2.5]" />
              </Button>

              <Button
                size="lg"
                variant="outline"
                onClick={scrollToServices}
                className="border border-white/15 bg-[#111C35]/70 backdrop-blur-sm text-white hover:bg-[#182647] hover:text-white hover:border-white/30 font-semibold px-7 py-6 text-base rounded-xl transition-all duration-300"
              >
                {heroData.secondaryCTA.text}
              </Button>
            </div>

            {/* 3 Sellos de Confianza (Trust Signals) */}
            <div
              className={`flex flex-wrap items-center gap-x-8 gap-y-4 scroll-animate delay-500 ${
                isVisible ? 'in-view' : ''
              }`}
            >
              {heroData.trustSignals.map((signal) => {
                const IconComponent = iconMap[signal.icon] || ShieldCheck;
                return (
                  <div key={signal.id} className="flex items-center gap-2.5">
                    <IconComponent
                      className="w-4 h-4 text-[#00C875] flex-shrink-0"
                      strokeWidth={2}
                    />
                    <span className="text-xs sm:text-sm text-slate-300 font-medium">
                      {signal.text}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Columna Derecha: Composición Orbital Google Cloud */}
          <div
            className={`lg:col-span-5 relative flex items-center justify-center min-h-[380px] sm:min-h-[450px] scroll-animate delay-300 ${
              isVisible ? 'in-view' : ''
            }`}
          >
            {/* Círculo Orbital Exterior */}
            <div className="absolute w-[310px] h-[310px] sm:w-[410px] sm:h-[410px] rounded-full border border-white/[0.07] pointer-events-none"></div>

            {/* Círculo Orbital Interior */}
            <div className="absolute w-[210px] h-[210px] sm:w-[280px] sm:h-[280px] rounded-full border border-white/[0.07] pointer-events-none"></div>

            {/* Tarjeta Satélite Superior Derecha: Seguridad */}
            <div className="absolute top-4 right-2 sm:top-6 sm:right-4 z-20 bg-[#13203B]/95 backdrop-blur-md border border-white/10 rounded-xl px-4 py-3 shadow-xl flex items-center gap-2.5">
              <ShieldCheck className="w-4 h-4 text-[#00C875]" strokeWidth={2.2} />
              <span className="text-xs sm:text-sm font-semibold text-white">
                Seguridad
              </span>
            </div>

            {/* Tarjeta Central: Google Cloud - Todo operando */}
            <div className="relative z-20 w-[280px] sm:w-[335px] bg-[#13203B]/95 backdrop-blur-md border border-white/10 rounded-2xl p-4 sm:p-5 shadow-2xl flex items-center justify-between">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-xl bg-[#00C875]/15 border border-[#00C875]/25 flex items-center justify-center flex-shrink-0">
                  <Cloud className="w-6 h-6 text-[#00C875]" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-xs text-slate-400 font-medium mb-0.5">
                    Google Cloud
                  </p>
                  <p className="text-base sm:text-lg font-bold text-white leading-tight">
                    Todo operando
                  </p>
                </div>
              </div>

              <div className="w-7 h-7 rounded-full bg-[#00C875] flex items-center justify-center flex-shrink-0 shadow-[0_0_14px_rgba(0,200,117,0.45)]">
                <Check className="w-4 h-4 text-[#041E13]" strokeWidth={3} />
              </div>
            </div>

            {/* Tarjeta Satélite Inferior Izquierda: Ahorro */}
            <div className="absolute bottom-8 left-0 sm:bottom-10 sm:left-2 z-20 bg-[#13203B]/95 backdrop-blur-md border border-white/10 rounded-xl px-4 py-3 shadow-xl flex items-center gap-2.5">
              <CircleDollarSign className="w-4 h-4 text-[#00C875]" strokeWidth={2.2} />
              <span className="text-xs sm:text-sm font-semibold text-white">
                Ahorro
              </span>
            </div>

            {/* Tarjeta Satélite Inferior Derecha: Disponibilidad 99.9% */}
            <div className="absolute bottom-2 right-0 sm:bottom-4 sm:right-2 z-20 bg-[#13203B]/95 backdrop-blur-md border border-white/10 rounded-2xl p-4 sm:p-5 shadow-xl w-[150px] sm:w-[170px]">
              <p className="text-xs text-slate-400 font-medium mb-1">
                Disponibilidad
              </p>
              <p className="text-2xl sm:text-[28px] font-bold text-[#00C875] leading-none mb-3">
                99.9%
              </p>
              <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className="w-[92%] h-full bg-[#00C875] rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Barra Inferior del Hero (como en landing_page.png) */}
      <div className="relative z-10 max-w-7xl w-full mx-auto px-6 sm:px-8 lg:px-12 pb-6 sm:pb-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs sm:text-sm text-slate-400/80">
        <span>Partner de confianza para tu siguiente etapa digital</span>

        <button
          onClick={scrollToServices}
          className="flex items-center gap-2.5 hover:text-slate-200 transition-colors group"
        >
          <span className="w-4 h-6 rounded-full border border-slate-500/70 flex items-start justify-center p-1 group-hover:border-[#00C875] transition-colors">
            <span className="w-0.5 h-1.5 bg-[#00C875] rounded-full animate-bounce"></span>
          </span>
          <span>Descubre cómo podemos ayudarte</span>
        </button>
      </div>
    </div>
  );
};

export default Hero;
